import SolrCore from './SolrCore';
import type SolrFq from './SolrFq';
import type SolrSort from './SolrSort';

// make sure to sync up commonFl with StandardDoc
const commonFl = ['id', '_type_s', 'payload_s'] as const;
type StandardDoc = {
  id: string;
  _type_s: string;
  payload_s: string;
};

type SolrQueryResponse = {
  response: {
    numFound: number;
    numFoundExact: boolean;
    start: number;
    docs: Array<StandardDoc>;
  };
  facet_counts?: {
    facet_fields?: Record<string, Array<string | number>>;
  };
};

/** a single facet bucket: the field value and how many docs matched it */
export type SolrFacetCount = {
  value: string;
  count: number;
};

type FacetConfig = {
  field: string;
  /** tag of an fq to exclude from this facet's counts (Solr `{!ex=...}`) */
  excludeTag?: string;
};

export default class SolrQuery {
  private readonly core: SolrCore;

  /** fq (filter query) */
  private fq?: SolrFq;

  /** defines the sort order */
  private sort?: SolrSort;

  private start: number;

  private rows: number;

  /** when set, request facet counts for this field */
  private facet?: FacetConfig;

  private constructor({ core }: { core: SolrCore }) {
    this.core = core;
    this.start = 0;
    this.rows = 50;
  }

  /** return a new SolrQuery with default values */
  public static devObj(): SolrQuery {
    return new SolrQuery({
      core: SolrCore.devObj,
    });
  }

  public static resourceCenter(): SolrQuery {
    return new SolrQuery({
      core: SolrCore.resourceCenter,
    });
  }

  /** Set the fq (filter query) for the Solr query */
  public setFq(fq: SolrFq): SolrQuery {
    this.fq = fq;
    return this;
  }

  /** Set the sort order for the Solr query */
  public setSort(sort: SolrSort): SolrQuery {
    this.sort = sort;
    return this;
  }

  public setRange({ start, rows }: { start: number; rows: number }): SolrQuery {
    this.start = start;
    this.rows = rows;
    return this;
  }

  /** Request facet counts for a field, optionally excluding a tagged fq from the counts */
  public setFacet({ field, excludeTag }: FacetConfig): SolrQuery {
    this.facet = { field, excludeTag };
    return this;
  }

  /** Should only be used in useSolrQuery */
  public async query<T>() {
    if (import.meta.server) {
      return {
        docs: [],
        numFound: 0,
        numFoundExact: true,
        start: 0,
        facetCounts: [] as Array<SolrFacetCount>,
      };
    }

    const org = document.location.origin;
    const qUrl = new URL(`${org}/api/s/${this.core.asString()}/select`);

    qUrl.searchParams.set('wt', 'json');
    qUrl.searchParams.set('q', '*:*');
    qUrl.searchParams.set('fl', commonFl.join(','));
    qUrl.searchParams.set('start', this.start.toString());
    qUrl.searchParams.set('rows', this.rows.toString());
    if (this.fq) {
      this.fq.setUrlParams(qUrl);
    }
    if (this.sort) {
      this.sort.setUrlParams(qUrl);
    }
    if (this.facet) {
      const facetPrefix = this.facet.excludeTag ? `{!ex=${this.facet.excludeTag}}` : '';
      qUrl.searchParams.set('facet', 'true');
      qUrl.searchParams.set('facet.field', `${facetPrefix}${this.facet.field}`);
      qUrl.searchParams.set('facet.mincount', '1');
    }

    const tic = Date.now();
    const result = await $fetch<SolrQueryResponse>(qUrl.href, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'include',
    })
      .catch((error) => {
        console.error('Error fetching Solr query:', error);
        return {
          response: {
            numFound: 0,
            numFoundExact: true,
            start: 0,
            docs: [],
          },
        };
      })
      .finally(() => {
        const toc = Date.now();
        console.log(`Solr query took ${toc - tic}ms`, qUrl.href);
      });

    const { response } = result;
    if (!response) {
      throw new Error('No response in Solr query');
    }
    const { docs, numFound, numFoundExact, start } = response;

    const parsedDocs = docs.map((doc) => JSON.parse(doc.payload_s) as T);
    const facetCounts = this.parseFacetCounts(result);

    return { docs: parsedDocs, numFound, numFoundExact, start, facetCounts };
  }

  /** Parse Solr's flat `[value, count, value, count]` facet array into typed buckets */
  private parseFacetCounts(result: SolrQueryResponse): Array<SolrFacetCount> {
    if (!this.facet) {
      return [];
    }
    const raw = result.facet_counts?.facet_fields?.[this.facet.field];
    if (!raw) {
      return [];
    }
    const buckets: Array<SolrFacetCount> = [];
    for (let ii = 0; ii < raw.length; ii += 2) {
      const value = raw[ii];
      const count = raw[ii + 1];
      if (typeof value === 'string' && typeof count === 'number') {
        buckets.push({ value, count });
      }
    }
    return buckets;
  }
}
